import {
  Badge,
  Box,
  Card,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { CalendarHeart } from "lucide-react";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MONTHLY_BDM_ATTENDANCE } from "../../constants";
import SpeedIcon from "@mui/icons-material/Speed";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const AttendanceReportComponent = ({}) => {
  const today = dayjs();
  const [selectedDate, setSelectedDate] = useState(today);
  const [displayMonth, setDisplayMonth] = useState(today.startOf("month"));
  const [attendance, setAttendance] = useState({});
  const [summary, setSummary] = useState([]);

  useEffect(() => {
    fetchAttendanceData(displayMonth);
  }, [displayMonth]);

  const fetchAttendanceData = async (monthDate) => {
    const year = monthDate.year();
    const month = monthDate.month() + 1; // Month is 0-indexed, so add 1
    const startDate = `${year}-${month.toString().padStart(2, "0")}-01`;

    let endDate;
    if (
      year > today.year() ||
      (year === today.year() && month > today.month() + 1)
    ) {
      setAttendance({}); // Don't fetch future months' data
      setSummary([]);
      return;
    }

    if (year === today.year() && month === today.month() + 1) {
      endDate = today.format("YYYY-MM-DD"); // Only show up to today's date
    } else {
      endDate = dayjs(startDate).endOf("month").format("YYYY-MM-DD");
    }

    try {
      const response = await axios.get(
        `${MONTHLY_BDM_ATTENDANCE}?bdmId=${localStorage.getItem(
          "username"
        )}&startDate=${startDate}&endDate=${endDate}`
      );
      setAttendance(response.data.dailyReports);
      setSummary(response.data.summary);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      if (
        error.response.data.message ===
        "No travel records found for the specified date range"
      ) {
        setAttendance({});
        setSummary([]);
      }
    }
  };

  // console.log(attendance);

  // Handle Date Selection
  const handleDateChange = (date) => {
    if (!date) return;
    setSelectedDate(dayjs(date));
    console.log("Selected Date:", dayjs(date).format("YYYY-MM-DD"));
  };

  // Handle Month Change
  const handleMonthChange = (newDate) => {
    if (!newDate) return;
    setDisplayMonth(dayjs(newDate).startOf("month"));
    setSelectedDate(dayjs(newDate).startOf("month"));
    console.log("Changed to Month:", dayjs(newDate).format("YYYY-MM"));
  };

  const getDaysToShow = () => {
    const selectedYear = displayMonth.year();
    const selectedMonth = displayMonth.month();

    if (
      selectedYear > today.year() ||
      (selectedYear === today.year() && selectedMonth > today.month())
    ) {
      return 0; // Future months show no data
    }
    if (selectedYear === today.year() && selectedMonth === today.month()) {
      return today.date(); // Current month shows only up to today's date
    }
    return dayjs(`${selectedYear}-${selectedMonth + 1}-01`).daysInMonth(); // Past months show all days
  };

  const calculateTotalHours = (inTime, outTime) => {
    if (!inTime || !outTime) return "N/A";

    const inDateTime = dayjs(`2024-01-01 ${inTime}`);
    const outDateTime = dayjs(`2024-01-01 ${outTime}`);

    if (!inDateTime.isValid() || !outDateTime.isValid()) return "N/A";

    const diff = outDateTime.diff(inDateTime, "minute");

    if (isNaN(diff) || diff < 0) return "N/A"; // Handling invalid differences

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return ""; // Only apply to month view

    const dateStr = dayjs(date).format("YYYY-MM-DD");
    const isSunday = dayjs(date).day() === 0;
    const dayOfWeek = dayjs(date).day(); // 0 = Sunday, 6 = Saturday

    // Ensure attendance is defined before accessing it
    if (!attendance || !Array.isArray(attendance)) return "";

    const attendanceData = attendance.find((item) => item.date === dateStr);
    const isPresent =
      attendanceData?.attendanceIn && attendanceData?.attendanceOut !== "N/A";
    const isAbsent = attendanceData && !isPresent;

    if (isPresent) return "present-dot";
    if (isAbsent) return "absent-dot";
    if (isSunday) return "holiday-dot";

    return "";
  };

  const [presentDays, setPresentDays] = useState(0);

  useEffect(() => {
    const attendanceArray = Array.isArray(attendance)
      ? attendance
      : Object.values(attendance);

    const presentCount = attendanceArray.filter(
      ({ attendanceIn, attendanceOut }) => {
        const totalHours = calculateTotalHours(attendanceIn, attendanceOut);
        return totalHours !== "N/A";
      }
    ).length;

    setPresentDays(presentCount);
  }, [attendance]);

  return (
    <>
      <Box p={3}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent={"center"}
        >
          <Grid item xs={12} sm={6}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Calendar
                  onChange={handleDateChange}
                  value={selectedDate.toDate()}
                  tileClassName={tileClassName}
                  onActiveStartDateChange={({ activeStartDate }) =>
                    handleMonthChange(activeStartDate)
                  }
                  className="custom-calendar"
                />
              </Box>
            </Card>
          </Grid>

          <style>
            {`
                  /* Calendar Container */
                  .custom-calendar {
                    width: 100% !important;
                    max-width: 100%;
                    height: auto;
                    padding: 10px;
                    font-family: "Poppins", sans-serif;
                    border-radius: 12px;
                  }

                  /* Date Cell Styles */
                  .react-calendar__tile {
                    padding: 4px;
                    text-align: center;
                    font-size: 14px;
                    transition: all 0.3s ease;
                  }

                  /* Hover Effect */
                  .react-calendar__tile:hover {
                    background: #f0f0f0;
                    cursor: pointer;
                  }

                  /* Remove background colors and add dots below the date */
                  .present-dot::after,
                  .absent-dot::after,
                  .holiday-dot::after {
                    content: "•"; /* Unicode character for a dot */
                    display: block;
                    font-size: 30px;
                  }

                  /* Present (Green Dot) */
                  .present-dot::after {
                    color: #0000FF; /* Green */
                  }

                  /* Absent (Red Dot) */
                  .absent-dot::after {
                    color: #dc3545; /* Red */
                  }

                  /* Holiday (Blue Dot) */
                  .holiday-dot::after {
                    color: #E1C16E; /* Golden */
                  }

                  /* Selected Date */
                  .react-calendar__tile--active {
                    background: #007bff !important;
                    color: white !important;
                    font-weight: bold;
                  }
                    
                  /* Ensure Saturdays don't appear red */
                    .react-calendar__month-view__days__day {
                      color: inherit !important; /* Keeps default color */
                    }
                `}
          </style>

          {/* 📌 Attendance Legends */}
          <Grid item xs={12} sm={12}>
            <Box display="flex" justifyContent="center" mt={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" mx={1}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#0000FF",
                    // bgcolor: "#28a745",
                    borderRadius: "50%",
                    mr: 1,
                  }}
                />
                <Typography variant="body2">Present</Typography>
              </Box>
              <Box display="flex" alignItems="center" mx={1}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#dc3545",
                    borderRadius: "50%",
                    mr: 1,
                  }}
                />
                <Typography variant="body2">Absent</Typography>
              </Box>
              <Box display="flex" alignItems="center" mx={1}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#E1C16E",
                    borderRadius: "50%",
                    mr: 1,
                  }}
                />
                <Typography variant="body2">Week-Off</Typography>
              </Box>
            </Box>
          </Grid>
          {/* Total Days in Month Card */}
          <Grid item xs={4} sm={6} md={4}>
            <Card
              sx={{ p: 1, boxShadow: 3, borderRadius: 3, position: "relative" }}
            >
              {/* <CalendarMonthIcon
                sx={{
                  fontSize: 35,
                  color: "primary.main",
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              /> */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="h6" fontWeight="bold" align="left">
                  All Days
                </Typography>
                <Typography variant="h5" align="left">
                  {summary?.totalDays || 0}
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Total Distance Card */}
          <Grid item xs={4} sm={6} md={4}>
            <Card
              sx={{ p: 1, boxShadow: 3, borderRadius: 3, position: "relative" }}
            >
              {/* <DirectionsCarIcon
                sx={{
                  fontSize: 35,
                  color: "primary.main",
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              /> */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="h6" fontWeight="bold" align="left">
                  Total KM
                </Typography>
                <Typography variant="h5" align="left">
                  {Math.round(summary?.grandTotalDistance) || 0}
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Average Distance Per Day Card */}
          {/* <Grid item xs={6} sm={6} md={4}>
            <Card
              sx={{ p: 1, boxShadow: 3, borderRadius: 3, position: "relative" }}
            >
              <SpeedIcon
                sx={{
                  fontSize: 35,
                  color: "primary.main",
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="h6" fontWeight="bold" align="left">
                  Avg KM/Day
                </Typography>
                <Typography variant="h5" align="left">
                  {summary?.averageDistancePerDay || 0}
                </Typography>
              </Box>
            </Card>
          </Grid> */}

          {/* Total Weekends Card */}
          <Grid item xs={4} sm={6} md={4}>
            <Card
              sx={{ p: 1, boxShadow: 3, borderRadius: 3, position: "relative" }}
            >
              {/* Icon positioned in the top-right corner */}
              {/* <CalendarHeart
                style={{
                  color: "#097969", // Adjust the color if needed
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
                size={30}
              /> */}
              {/* Content with flexbox to align on the left */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="h6" fontWeight="bold" align="left">
                  Present
                </Typography>
                <Typography variant="h5" align="left">
                  {presentDays}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Attendance Cards */}
        <Grid container spacing={2} mt={2}>
          {!attendance || Object.keys(attendance).length === 0 ? (
            <Typography
              variant="h6"
              sx={{ mt: 2, textAlign: "center", width: "100%" }}
            >
              No attendance data available
            </Typography>
          ) : getDaysToShow() === 0 ? (
            <Typography
              variant="h6"
              sx={{ mt: 2, textAlign: "center", width: "100%" }}
            >
              No data available for future months
            </Typography>
          ) : (
            Array.from({ length: getDaysToShow() }, (_, i) => {
              const date = dayjs(
                `${displayMonth.year()}-${displayMonth.month() + 1}-${i + 1}`
              ).format("YYYY-MM-DD");
              const dayName = dayjs(date).format("ddd");

              // Find attendance data for the current date
              const attendanceArray = Array.isArray(attendance)
                ? attendance
                : Object.values(attendance);
              const attendanceData =
                attendanceArray.find((item) => item.date === date) || {};
              const inTime = attendanceData.attendanceIn || "N/A";
              const outTime = attendanceData.attendanceOut || "N/A";
              const inTimeFormatted =
                inTime && inTime !== "N/A"
                  ? dayjs(inTime, "HH:mm:ss").format("HH:mm")
                  : "--:--";
              const outTimeFormatted =
                outTime && outTime !== "N/A"
                  ? dayjs(outTime, "HH:mm:ss").format("HH:mm")
                  : "--:--";
              const totalDistance = attendanceData.totalDistance || 0;
              const totalHours = calculateTotalHours(inTime, outTime);

              return (
                <Grid item xs={12} key={date}>
                  <Card
                    sx={{
                      p: 2,
                      boxShadow: 3,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    {/* Date Box with Day */}
                    <Box
                      sx={{
                        backgroundColor:
                          dayName === "Sun"
                            ? totalHours !== "N/A"
                              ? "#0000FF" // Sunday with valid totalHours
                              : inTime !== "N/A" || outTime !== "N/A"
                              ? "red" // Sunday with N/A totalHours but at least one time present
                              : "#E1C16E" // Sunday with both inTime & outTime as N/A
                            : totalHours === "N/A"
                            ? "red" // Other days with N/A totalHours
                            : "#0000FF", // Other days with valid totalHours
                        color: "#fff",
                        textAlign: "center",
                        borderRadius: "8px",
                        padding: "8px",
                        fontWeight: "bold",
                        minWidth: "50px",
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {dayjs(date).format("DD")}
                      </Typography>
                      <Typography variant="body2">{dayName}</Typography>
                    </Box>

                    {/* Time Row */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h6" fontWeight="bold">
                        In
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {inTimeFormatted || "--:--"}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h6" fontWeight="bold">
                        Out
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {outTimeFormatted || "--:--"}
                      </Typography>
                    </Box>

                    {/* Total Hours & KM Section */}
                    <Box
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        gap: 3, // Provides sufficient spacing between Total and KM
                        minWidth: "120px", // Ensures consistent width for alignment
                        justifyContent: "center", // Centers content for improved visual balance
                      }}
                    >
                      <Box>
                        <Typography
                          color="primary"
                          variant="h6"
                          fontWeight="bold"
                        >
                          Total
                        </Typography>
                        <Typography
                          color="primary"
                          variant="body1"
                          fontWeight="bold"
                        >
                          {totalHours}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          color="primary"
                          variant="h6"
                          fontWeight="bold"
                        >
                          KM
                        </Typography>
                        <Typography
                          color="primary"
                          variant="body1"
                          fontWeight="bold"
                        >
                          {totalDistance}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            }).reverse()
          )}
        </Grid>
      </Box>
    </>
  );
};

export default AttendanceReportComponent;
